/* eslint-disable camelcase */
/* eslint-disable no-shadow */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { Badge, Dropdown, Menu } from 'antd';
import { GiftOutlined } from '@ant-design/icons';
import { parseFullName } from 'parse-full-name';
import { openModal } from '../../actions/modal';
import { changeDefaultOrganization } from '../../actions/appActions';
import { getLoginInfo } from '../../actions/auth';
import {
  hasFeatureInLocalStorage,
  NOTIFICATION_PREVIEW_FEATURE,
  verifyFeatureAccess,
} from '../../global/featureFlags';
// Using Dropdown from antd means the redesign
// Button's props of 'type' and 'size' must be
// Applied to the its className since the
// Dropdown doesn't apply childrens' styles properly
import MedsenderIcon from '../../assets/img/redesign/medsender_icon.png';
import Avatar from '../../components/Avatar';
import Typography from '../../components/Typography';
import Button from '../../components/Button';
import MobileOptions from './MobileOptions';
import {
  PlusSvg,
  ContactSvg,
  UploadSvg,
  HouseSvg,
  NotificationSvg,
} from '../../assets/img/redesign/svgPaths';
import './newHeader.scss';
import { getNotifications } from '../../actions/notifications';
import { captureAnalytics } from '../../actions/preload';
import useNotificationRefresh from '../../hooks/useNotificationRefresh';

const formatNotificationTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const NotificationBell = ({ notifications, onRefresh }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isOpen = isHovered || isFocused;

  const showNotifications = () => {
    setIsHovered(true);
    onRefresh();
  };

  const handleBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsFocused(false);
    }
  };

  return (
    <div
      className="notification-bell"
      onMouseEnter={showNotifications}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
    >
      <Badge size="small" count={notifications.length} offset={[-10, 10]}>
        <Button
          type="tertiary"
          size="medium"
          icon={NotificationSvg}
          aria-label="Notifications"
          title="Notifications"
          aria-expanded={isOpen}
          onClick={() => window.location.assign('/app/unread_messages')}
        />
      </Badge>
      {isOpen && (
        <div className="notification-bell__popover" role="status">
          <div className="notification-bell__title">Notifications</div>
          {notifications.length === 0 && (
            <div className="notification-bell__empty">You’re all caught up.</div>
          )}
          {notifications.slice(0, 12).map((notification) => {
            const isAssigned = notification.type === 'assigned_record';
            // ENG-3340: computed failed-fax entries, present only with the feature on.
            const isFailedFax = notification.type === 'failed_fax';
            let notificationTitle = 'New message';
            if (isAssigned) notificationTitle = 'Assigned to me';
            if (isFailedFax) notificationTitle = 'Fax not delivered';
            let notificationType = 'message';
            if (isAssigned) notificationType = 'assigned_record';
            if (isFailedFax) notificationType = 'failed_fax';
            return (
              <a
                className="notification-bell__item"
                href={notification.link || '/app/unread_messages'}
                key={notification.notification_id || notification.send_token || notification.message}
                onClick={() => captureAnalytics('notification_preview_item_clicked', {
                  notification_type: notificationType,
                })}
              >
                <span className="notification-bell__item-heading">
                  <span className="notification-bell__item-title">
                    {notificationTitle}
                  </span>
                  {notification.timestamp && (
                    <span className="notification-bell__item-time">
                      {formatNotificationTimestamp(notification.timestamp)}
                    </span>
                  )}
                </span>
                <span className="notification-bell__item-message">{notification.message}</span>
              </a>
            );
          })}
          <div className="notification-bell__footer">
            <a
              href="/app/unread_messages"
              onClick={() => captureAnalytics('notification_preview_view_all_clicked')}
            >
              View messages
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

NotificationBell.propTypes = {
  notifications: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRefresh: PropTypes.func.isRequired,
};

const pageNames = {
  '/app': 'Home',
  '/app/contacts': 'Contacts',
  '/app/uploads': 'Uploads',
  '/app/sent': 'Sent',
  '/app/received': 'Inbox',
  '/app/assigned': 'Assigned',
  '/app/referred': 'Referral Management',
  '/app/approvals': 'Approvals',
  '/app/user': 'Settings',
  '/app/guide': 'Guide',
  '/app/unread_messages': 'Messages',
  '/app/voice_inbox': 'MAIRA Voice Dashboard',
  '/app/analytics': 'Analytics',
  '/app/referral_portal': 'Referral Portal',
  '/app/arm': 'ARM',
  '/app/engagement': 'Patient Engagement',
};

const LoggedInHeader = ({
  ...props
}) => {
  const posthog = usePostHog();
  const [canAddContact, setCanAddContact] = useState(false);
  const [hasNoFeatures, setHasNoFeatures] = useState(false);
  const [currentOrganization, setCurrentOrganization] = useState('');
  const [organizationMenu, setOrganizationMenu] = useState(null);
  const onMobile = window.innerWidth < 800;
  const referCopy = 'Refer & Earn';
  const notificationPreviewEnabled = hasFeatureInLocalStorage(NOTIFICATION_PREVIEW_FEATURE);

  const {
    user,
    isAdmin,
    isTrialUser,
    organizationDetails: { organizations, current_organization },
    currentPath,
    impersonating,
    getLoginInfo,
    changeDefaultOrganization,
    getNotifications,
  } = props;

  useNotificationRefresh(
    notificationPreviewEnabled ? props.cable : null,
    notificationPreviewEnabled ? props.getNotifications : null,
  );

  useEffect(() => {
    if (impersonating) return; // Don't track analytics during impersonation
    if (
      user?.uid
      && posthog
      && process.env.NODE_ENV !== 'development'
    ) {
      posthog.identify(
        user?.uid,
        {
          name: user?.fullName,
          organizationName: user?.organization,
          autoUploadEmr: user?.autoUploadEmr,
          availableEmrs: user?.availableEmrs,
          currentOrganization: current_organization,
          organizations,
          documentCategories: user?.documentCategories,
          associatedFaxNumbers: user?.associatedFaxNumbers,
          referralManagement: user?.referralManagement,
          isTrialUser,
          isAdmin,
        },
      );
    }
  }, [user, posthog, current_organization, organizations, isTrialUser, isAdmin, impersonating]);

  const reloadPage = useCallback(async () => {
    await getLoginInfo();
    window.location.reload();
  }, [getLoginInfo]);

  const handleChangeDefaultOrganization = useCallback(async (org) => {
    setCurrentOrganization(org);
    await changeDefaultOrganization(org, reloadPage);
  }, [changeDefaultOrganization, reloadPage]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const canModify = !(
        (await verifyFeatureAccess('admin_lock_address_book')) && !isAdmin
      );
      const noFeatures = await verifyFeatureAccess('no_features');
      if (isMounted) {
        setHasNoFeatures(noFeatures);
        setCanAddContact(canModify);
        // Home and Messages refresh notifications on every mount. Let those
        // routes own the request; the header supplies it on other entry pages.
        // Auth restoration can move /login to /app while these checks await.
        const pathname = window.location.pathname.replace(/\/$/, '').toLowerCase();
        if (!['/app', '/app/unread_messages'].includes(pathname)) {
          getNotifications();
        }
      }

      if (isMounted && current_organization) {
        setCurrentOrganization(current_organization);
      }

      if (isMounted && organizations) {
        setOrganizationMenu(
          <Menu>
            {organizations.map((org) => (
              <Menu.Item key={org.name} onClick={() => handleChangeDefaultOrganization(org.name)}>
                {org.name}
              </Menu.Item>
            ))}
          </Menu>,
        );
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [current_organization, organizations, isAdmin, notificationPreviewEnabled, getNotifications, handleChangeDefaultOrganization]);

  const avatarContent = user?.name
    ?.split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('') || '';

  // Handler for the Upload Record button. Shows the modal to upload new record.
  const openUploadModal = async () => {
    const canUpload = await verifyFeatureAccess('upload_record');
    const modal = canUpload ? 'UPLOAD_RECORD' : 'RAISE_FLAG';
    const modalType = {
      type: modal,
      ...(!canUpload && { featureFlag: 'upload_record' }),
    };

    props.openModal(modalType);
  };

  const handleReferFriend = () => {
    if (impersonating) return;
    const openGrowSurf = () => {
      // Call addParticipant immediately before open for in-memory registration
      // This works even in Safari since we're not relying on persistent storage
      if (user?.uid) {
        try {
          const parsedName = parseFullName(user?.fullName);
          const firstName = parsedName.first;
          const lastName = parsedName.last;
          window.growsurf.addParticipant({
            email: user.uid,
            firstName,
            lastName,
          });
        } catch (error) {
          console.error('Error adding participant:', error);
        }
        window.growsurf.open();
      } else {
        window.growsurf.open();
      }
    };

    if (window.growsurf && typeof window.growsurf.open === 'function') {
      openGrowSurf();
    } else {
      let handled = false;
      let timeout;

      const grsfReadyHandler = () => {
        if (handled) return;
        handled = true;
        clearTimeout(timeout);
        window.removeEventListener('grsfReady', grsfReadyHandler);
        openGrowSurf();
      };

      timeout = setTimeout(() => {
        if (handled) return;
        handled = true;
        window.removeEventListener('grsfReady', grsfReadyHandler);
        if (window.growsurf && typeof window.growsurf.open === 'function') {
          openGrowSurf();
        } else {
          // eslint-disable-next-line no-alert
          window.alert('Referral program is temporarily unavailable. Please try again later.');
        }
      }, 5000);

      window.addEventListener('grsfReady', grsfReadyHandler, { once: true });
    }
  };

  const menu = (
    <Menu>
      <Menu.Item onClick={openUploadModal} data-tutorial="new-menu-upload">
        <span className="dropdown-icon">{UploadSvg}</span>
        Upload
      </Menu.Item>
      {canAddContact && (
        <Menu.Item onClick={() => window.location.assign('/app/contacts?add=true')}>
          <span className="dropdown-icon">{ContactSvg}</span>
          Contact
        </Menu.Item>
      )}
    </Menu>
  );

  const disableNewButton = hasNoFeatures;
  const handleLogoClick = (event) => {
    if (hasNoFeatures) {
      event.preventDefault();
    }
  };

  return (
    <>
      <div className="left">
        <a href="/app" onClick={handleLogoClick} aria-disabled={hasNoFeatures}>
          <img src={MedsenderIcon} width="30px" alt="fullLogo.png" />
        </a>
        <Typography variant="text" size="large" bold>
          {pageNames[currentPath]}
        </Typography>
        <Button
          className="tertiary small"
          leftIcon={<GiftOutlined style={{ fontSize: '20px' }} />}
          label={referCopy}
          onClick={handleReferFriend}
        />
        {organizations.length > 1 && (
          <Dropdown overlay={organizationMenu} trigger={['click']}>
            <Button className="tertiary medium" leftIcon={HouseSvg} label={currentOrganization} />
          </Dropdown>
        )}
      </div>
      <div className="right">
        <Dropdown overlay={menu} trigger={['click']} disabled={disableNewButton}>
          <Button
            className="primary medium"
            label="New"
            leftIcon={PlusSvg}
            data-tutorial="new-menu"
            disabled={disableNewButton}
          />
        </Dropdown>
        {notificationPreviewEnabled ? (
          <NotificationBell
            notifications={props.notifications}
            onRefresh={props.getNotifications}
          />
        ) : (
          <Badge size="small" count={props.notifications.length} offset={[-10, 10]}>
            <Button
              type="tertiary"
              size="medium"
              icon={NotificationSvg}
              aria-label="Notifications"
              title="Notifications"
              onClick={() => window.location.assign('/app/unread_messages')}
            />
          </Badge>
        )}
        <Avatar
          size="small"
          content={avatarContent}
          name={user?.fullName}
          org={user?.organization}
          header
        />
        {onMobile && (
          <MobileOptions />
        )}
      </div>
    </>
  );
};

LoggedInHeader.defaultProps = {
  user: {},
};

LoggedInHeader.propTypes = {
  user: PropTypes.object,
  isTrialUser: PropTypes.bool.isRequired,
  organizationDetails: PropTypes.shape({
    organizations: PropTypes.array.isRequired,
    current_organization: PropTypes.string,
  }).isRequired,
  isAdmin: PropTypes.bool.isRequired,
  currentPath: PropTypes.string.isRequired,
  notifications: PropTypes.array.isRequired,
  cable: PropTypes.shape({
    subscriptions: PropTypes.shape({
      create: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,

  impersonating: PropTypes.bool.isRequired,
  openModal: PropTypes.func.isRequired,
  changeDefaultOrganization: PropTypes.func.isRequired,
  getLoginInfo: PropTypes.func.isRequired,
  getNotifications: PropTypes.func.isRequired,
};

export default connect((state) => ({
  user: state.auth.data,
  isTrialUser: state.auth.trialUser,
  organizationDetails: state.auth.organizationDetails,
  isAdmin: state.auth.isAdmin,
  currentPath: state.appState.currentPath,
  notifications: state.notifications.notifications,
  impersonating: state.auth.impersonating,
}), {
  openModal,
  changeDefaultOrganization,
  getLoginInfo,
  getNotifications,
})(withRouter(LoggedInHeader));
